import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, memo } from 'react';
import { validationSchema } from './constants';
import { PmiButton } from 'shell/components/shared/button';
import { PmiTextField } from 'shell/components/shared/text-field';
import MicroServiceList from './microservice-list';
import PostCalculationList from './post-calculation-list';
import SchedulationTable, { SchedulationItemVM } from './schedulation-table';
import ParameterTable, { ParameterItemVM } from './parameter-table';

export interface BatchManagementForm {
  id?: string;
  batchName?: string;
  description?: string;
  active?: boolean;
  microservice?: string;
  inputFolder?: string;
  outputFolder?: string;
  executionText?: string;
  postCalculation?: string;
  mailingList?: string;
  parameters?: Array<ParameterItemVM>;
  schedulations?: Array<SchedulationItemVM>;
}

interface FormBatchManagementProps {
  onOk: (form: BatchManagementForm) => void;
  onCancel: (form?: BatchManagementForm) => void;
  title: string;
  disabled?: boolean;
  isEdit?: boolean;
  initialValues?: BatchManagementForm;
}

const FormBatchManagementComponent = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  isEdit = false,
  initialValues,
}: FormBatchManagementProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const filtersBatchManagementForm = useFormik<BatchManagementForm>({
    initialValues: initialValues || {
      id: '',
      active: false,
      description: '',
      batchName: '',
      inputFolder: '',
      outputFolder: '',
      executionText: '',
      mailingList: '',
      microservice: '',
      postCalculation: 'NEVER',
      parameters: [],
      schedulations: [],
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={filtersBatchManagementForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Nome Batch"
                  label="Nome Batch"
                  id="batchName"
                  name="batchName"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={filtersBatchManagementForm.values.batchName || ''}
                  disabled={disabled || isEdit}
                  onChange={filtersBatchManagementForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="Descrizione"
                  label="Descrizione"
                  id="description"
                  name="description"
                  fullWidth
                  inputProps={{ maxLength: '100' }}
                  type="text"
                  variant="outlined"
                  value={filtersBatchManagementForm.values.description || ''}
                  disabled={disabled}
                  onChange={filtersBatchManagementForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="active"
                      name="active"
                      disabled={disabled}
                      checked={filtersBatchManagementForm.values.active || false}
                      onChange={() =>
                        filtersBatchManagementForm.setFieldValue(
                          'active',
                          !filtersBatchManagementForm.values.active
                        )
                      }
                      inputProps={{ 'aria-label': 'Attivo' }}
                    />
                  }
                  label="Attivo"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <MicroServiceList
                  value={filtersBatchManagementForm.values.microservice || ''}
                  disabled={disabled}
                  onChange={(event) =>
                    filtersBatchManagementForm.setFieldValue('microservice', event?.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <PmiTextField
                  aria-label="Specifiche di esecuzione"
                  label="Specifiche di esecuzione"
                  id="executionText"
                  name="executionText"
                  fullWidth
                  inputProps={{ maxLength: '100' }}
                  type="text"
                  variant="outlined"
                  value={filtersBatchManagementForm.values.executionText || ''}
                  disabled={disabled}
                  onChange={filtersBatchManagementForm.handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Cartella di input"
                  label="Cartella di input"
                  id="inputFolder"
                  name="inputFolder"
                  fullWidth
                  inputProps={{ maxLength: '100' }}
                  type="text"
                  variant="outlined"
                  value={filtersBatchManagementForm.values.inputFolder || ''}
                  disabled={disabled}
                  onChange={filtersBatchManagementForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Cartella di output"
                  label="Cartella di output"
                  id="outputFolder"
                  name="outputFolder"
                  fullWidth
                  inputProps={{ maxLength: '100' }}
                  type="text"
                  variant="outlined"
                  value={filtersBatchManagementForm.values.outputFolder || ''}
                  disabled={disabled}
                  onChange={filtersBatchManagementForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PostCalculationList
                  value={filtersBatchManagementForm.values.postCalculation || ''}
                  disabled={disabled}
                  onChange={(event) =>
                    filtersBatchManagementForm.setFieldValue('postCalculation', event?.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="Mailing list (;)"
                  label="Mailing list (;)"
                  id="mailingList"
                  name="mailingList"
                  fullWidth
                  inputProps={{ maxLength: '100' }}
                  type="text"
                  variant="outlined"
                  value={filtersBatchManagementForm.values.mailingList || ''}
                  disabled={disabled}
                  onChange={filtersBatchManagementForm.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <ParameterTable
                  jobName={filtersBatchManagementForm?.values?.id || initialValues?.id}
                  body={filtersBatchManagementForm.values.parameters || []}
                  onConfirm={(parameters) =>
                    filtersBatchManagementForm.setFieldValue('parameters', parameters)
                  }
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12}>
                <SchedulationTable
                  jobName={filtersBatchManagementForm?.values?.id || initialValues?.id}
                  body={filtersBatchManagementForm.values.schedulations || []}
                  onConfirm={(schedulations) =>
                    filtersBatchManagementForm.setFieldValue('schedulations', schedulations)
                  }
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit">
            {disabled ? 'Esegui Ora' : 'Conferma'}
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(filtersBatchManagementForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormBatchManagementComponent.displayName = 'Form Batch management component';

const FormBatchManagement = memo(FormBatchManagementComponent);

export default FormBatchManagement;
